import type Controller from '@ember/controller';
import { inject as controller } from '@ember/controller';
import Component from '@glimmer/component';
import { PrismBreadcrumb } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import RoomHierarchyTable from 'lumen/components/settings/recommendations/room-hierarchy/table-room-hierarchy';
import RoomHierarchyStrategyCard from 'lumen/components/settings/recommendations/room-hierarchy-strategy-card';
import type { RecommendationStrategy } from 'lumen/models';
import type HotelSettingsRecommendationsRoomHierarchyRoute from 'lumen/routes/hotel/settings/recommendations/room-hierarchy';
import type { ModelFrom } from 'lumen/utils/routes';

interface SettingsRecommendationsRoomHierarchySignature {
  Args: {
    model: ModelFrom<HotelSettingsRecommendationsRoomHierarchyRoute>;
  };
}

export default class SettingsRecommendationsRoomHierarchy extends Component<SettingsRecommendationsRoomHierarchySignature> {
  @controller('hotel.settings.recommendations')
  declare recommendationsController: Controller & {
    strategiesPerType: {
      roomOffsetHierarchyStrategy: RecommendationStrategy;
    };
  };

  get roomOffsetHierarchyStrategy(): RecommendationStrategy {
    return this.recommendationsController.strategiesPerType
      .roomOffsetHierarchyStrategy;
  }

  <template>
    <SettingsPageTemplate
      @description={{t
        "pricing_assistant.recommendations.settings.room_hierarchy.description"
      }}
      @isFullHeight={{true}}
      @title={{t
        "pricing_assistant.recommendations.settings.room_hierarchy.title"
      }}
    >
      <:breadcrumb>
        <PrismBreadcrumb as |B|>
          <B.item
            @label={{t "pricing_assistant.recommendations.settings.main_title"}}
            @route="hotel.settings.recommendations.index"
          />
          <B.item
            @isCurrent={{true}}
            @label={{t
              "pricing_assistant.recommendations.settings.room_hierarchy.title"
            }}
          />
        </PrismBreadcrumb>
      </:breadcrumb>
      <:link>
        <a
          class="prism-link"
          href="https://help.mylighthouse.com/en/articles/7857322-how-do-i-customize-my-recommendations"
          rel="noopener noreferrer"
          target="_blank"
        >{{t "general.learn_more"}}</a>
      </:link>
      <:default>
        <RoomHierarchyStrategyCard
          @saveStrategyTask={{@model.saveStrategyTask}}
          @strategy={{this.roomOffsetHierarchyStrategy}}
          @title={{t
            "pricing_assistant.recommendations.settings.room_hierarchy.title"
          }}
          as |isEditMode|
        >
          <RoomHierarchyTable
            @isEditMode={{isEditMode}}
            @strategy={{this.roomOffsetHierarchyStrategy}}
          />
        </RoomHierarchyStrategyCard>
      </:default>
    </SettingsPageTemplate>
  </template>
}
